import React from 'react';
import '../styles/PlacaVeicular.css';

/**
 * PlacaVeicular — desenha a placa do veículo como ela é no para-brisa.
 * CI Sistemas de Informática Ltda. — Onda 70.
 *
 * ─── Por que desenhar, e não só escrever o texto ────────────────────────────
 *
 * A placa é o objeto central deste sistema: é por ela que o veículo entra, é
 * ela que o operador confere contra o carro parado à frente dele, e é ela que
 * aparece em toda tela de cobrança. Escrita como texto comum, some no meio dos
 * outros dados; desenhada, o operador reconhece de relance e compara com o que
 * está vendo pela janela.
 *
 * O desenho também carrega informação que o texto não carrega: **o modelo da
 * placa**. Mercosul e padrão antigo têm formatos distintos (ABC1D23 contra
 * ABC1234), e a diferença aparece sozinha na tela, sem ninguém precisar
 * explicar.
 *
 * ─── Sobre as cores ─────────────────────────────────────────────────────────
 *
 * Este é o único componente da plataforma que declara cor fora do sistema de
 * tokens, e é de propósito: são as cores oficiais do objeto físico, definidas
 * pela Resolução CONTRAN nº 780/2019 e pelas normas anteriores. Uma placa
 * Mercosul é branca com tarja azul, e uma placa antiga é cinza — repintá-las
 * com o acento da interface faria o desenho deixar de ser reconhecível, que é a
 * única razão de ele existir. Vale a mesma lógica de uma bandeira num seletor
 * de idioma.
 */

// Mercosul: três letras, um número, uma letra, dois números.
const MERCOSUL = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/;
// Padrão antigo: três letras e quatro números.
const ANTIGA = /^[A-Z]{3}[0-9]{4}$/;

export function modeloDaPlaca(placa) {
  const p = String(placa || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (MERCOSUL.test(p)) return 'mercosul';
  if (ANTIGA.test(p)) return 'antiga';
  return 'desconhecido';
}

/** Agrupa como se lê: ABC 1D23 no Mercosul, ABC-1234 na antiga. */
function formatar(placa, modelo) {
  const p = String(placa || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (modelo === 'mercosul') return `${p.slice(0, 3)}${p.slice(3)}`;
  if (modelo === 'antiga')   return `${p.slice(0, 3)}-${p.slice(3)}`;
  return p;
}

/**
 * @param {string} placa    A placa, com ou sem separadores.
 * @param {string} tamanho  'p' na lista do pátio, 'm' padrão, 'g' no atendimento.
 * @param {string} pais     Texto da tarja. O padrão é BRASIL.
 */
export default function PlacaVeicular({ placa, tamanho = 'm', pais = 'BRASIL', title }) {
  const modelo = modeloDaPlaca(placa);
  const texto = formatar(placa, modelo);

  // Proporção real da placa brasileira: 400 × 130 mm.
  const alturas = { p: 26, m: 38, g: 62 };
  const altura = alturas[tamanho] || alturas.m;
  const largura = Math.round(altura * (400 / 130));

  const rotulo = `Placa ${texto}${modelo !== 'desconhecido' ? `, padrão ${modelo === 'mercosul' ? 'Mercosul' : 'anterior'}` : ''}`;

  // A tarja ocupa 26 dos 130 mm de altura.
  const tarja = 26;
  const cores = modelo === 'antiga'
    ? { fundo: '#d9d9d9', borda: '#1a1a1a', tinta: '#111111', tarja: '#b7b7b7', tarjaTinta: '#111111' }
    : { fundo: '#ffffff', borda: '#1a1a1a', tinta: '#111111', tarja: '#003399', tarjaTinta: '#ffffff' };

  return (
    <svg
      className={`pv-placa pv-${modelo} pv-${tamanho}`}
      width={largura}
      height={altura}
      viewBox="0 0 400 130"
      role="img"
      aria-label={rotulo}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title || rotulo}</title>

      {/* Corpo da placa */}
      <rect x="3" y="3" width="394" height="124" rx="14" ry="14"
            fill={cores.fundo} stroke={cores.borda} strokeWidth="6" />

      {/* Tarja superior */}
      <path
        d={`M 3 17 A 14 14 0 0 1 17 3 L 383 3 A 14 14 0 0 1 397 17 L 397 ${tarja + 3} L 3 ${tarja + 3} Z`}
        fill={cores.tarja}
      />
      <line x1="3" y1={tarja + 3} x2="397" y2={tarja + 3} stroke={cores.borda} strokeWidth="3" />

      {modelo === 'antiga' ? (
        // A placa anterior traz município e UF na tarja. O sistema não guarda
        // essa informação, então a tarja fica lisa em vez de inventar um texto.
        null
      ) : (
        <>
          {/* Bandeira do Mercosul, à esquerda: campo azul com estrelas. */}
          <rect x="14" y="8" width="30" height="17" fill="#0f2f7f" stroke="#ffffff" strokeWidth="1.5" />
          {[0, 1, 2, 3].map(i => (
            <circle key={i} cx={21 + i * 5.6} cy={i % 2 === 0 ? 14 : 19} r="1.5" fill="#ffffff" />
          ))}

          <text
            x="200" y={tarja - 5}
            textAnchor="middle"
            fill={cores.tarjaTinta}
            fontFamily="Arial Narrow, Arial, Helvetica, sans-serif"
            fontSize="17"
            fontWeight="700"
            letterSpacing="2.5"
          >
            {pais}
          </text>

          {/* Bandeira do Brasil, à direita. */}
          <g>
            <rect x="356" y="8" width="30" height="17" fill="#009c3b" stroke="#ffffff" strokeWidth="1.5" />
            <path d="M 371 10.5 L 383 16.5 L 371 22.5 L 359 16.5 Z" fill="#ffdf00" />
            <circle cx="371" cy="16.5" r="4" fill="#002776" />
          </g>
        </>
      )}

      {/* Os caracteres. A fonte oficial é a FE-Schrift, que não está disponível
          no navegador; a pilha condensada abaixo é a aproximação mais próxima e
          mantém os dígitos com a mesma largura. */}
      <text
        x="200" y="108"
        textAnchor="middle"
        fill={cores.tinta}
        fontFamily="'Arial Narrow', 'Roboto Condensed', 'DejaVu Sans Condensed', Arial, sans-serif"
        fontSize="72"
        fontWeight="700"
        letterSpacing={modelo === 'antiga' ? '3' : '4'}
      >
        {texto}
      </text>
    </svg>
  );
}
